import { useEffect, useState } from "react";
import api, { formatApiError, getApiEmpresa } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

// Pestañas de incidencias de escuela para la página Asistencia docente: permisos por
// clase, suspensiones, clases extra y suplencias. El backend está en
// routes/incidencias_escuela.py y el efecto en la nómina lo calcula asistencia_docente.py.

const SIN = "__ninguno__";
const conEmpresa = (data) => ({ ...data, empresa_id: getApiEmpresa() || undefined });

function useListado(ruta, desde, hasta, recargar) {
  const [items, setItems] = useState([]);
  const cargar = () =>
    api.get(ruta, { params: { fecha_desde: desde, fecha_hasta: hasta } })
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruta, desde, hasta, recargar]);
  return [items, cargar];
}

async function borrar(ruta, id, onDone) {
  if (!window.confirm("¿Eliminar este registro? La nómina se recalcula sin él.")) return;
  try {
    await api.delete(`${ruta}/${id}`);
    toast.success("Eliminado");
    onDone();
  } catch (e) {
    toast.error(formatApiError(e.response?.data?.detail));
  }
}

function Tabla({ columnas, filas, vacio, editable, onBorrar, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
      <table className="data-table" data-testid={testid}>
        <thead>
          <tr>{columnas.map((c) => <th key={c}>{c}</th>)}{editable && <th></th>}</tr>
        </thead>
        <tbody>
          {filas.length === 0 && <tr><td colSpan={columnas.length + 1} className="text-center text-slate-500 py-8">{vacio}</td></tr>}
          {filas.map((f) => (
            <tr key={f.id}>
              {f.celdas.map((c, i) => <td key={i}>{c}</td>)}
              {editable && (
                <td><Button variant="ghost" size="sm" onClick={() => onBorrar(f.id)}><Trash size={16} className="text-red-600" /></Button></td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Campo({ label, children, ancho }) {
  return (
    <div className={`space-y-1 ${ancho ? "sm:col-span-2" : ""}`}>
      <Label className="label-uppercase">{label}</Label>
      {children}
    </div>
  );
}

function SelectSimple({ value, onChange, opciones, placeholder, testid }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder || "Selecciona"} /></SelectTrigger>
      <SelectContent>{opciones.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function FormDialog({ open, titulo, onClose, onSubmit, children }) {
  const [busy, setBusy] = useState(false);
  const enviar = async () => {
    setBusy(true);
    try {
      await onSubmit();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle className="font-display">{titulo}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button className="bg-brand hover:bg-brand-hover" onClick={enviar} disabled={busy} data-testid="incid-guardar">
            {busy ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- permisos por clase ----------------
export function PermisosTab({ desde, hasta, cat, editable, onCambio }) {
  const [items, cargar] = useListado("/escuela/permisos-clase", desde, hasta);
  const [form, setForm] = useState(null);
  const [clasesDia, setClasesDia] = useState([]);

  useEffect(() => {
    if (!form?.empleado_id || !form?.fecha) return setClasesDia([]);
    api.get(`/escuela/cargas/${form.empleado_id}/dia`, { params: { fecha: form.fecha } })
      .then((r) => setClasesDia(r.data)).catch(() => setClasesDia([]));
  }, [form?.empleado_id, form?.fecha]);

  const guardar = async () => {
    await api.post("/escuela/permisos-clase", conEmpresa(form));
    toast.success("Permiso registrado");
    setForm(null);
    cargar();
    onCambio();
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <p className="text-sm text-slate-600">Permiso para algunas clases del día. Esas clases no requieren marca y el resto se evalúa normal.</p>
        {editable && (
          <Button className="bg-brand hover:bg-brand-hover" onClick={() => setForm({ empleado_id: "", fecha: desde, clase_ids: [], con_goce: true, motivo: "" })} data-testid="permiso-nuevo">
            <Plus size={16} className="mr-2" /> Nuevo permiso
          </Button>
        )}
      </div>
      <Tabla
        testid="permisos-tabla"
        columnas={["Fecha", "Docente", "Clases", "Goce", "Motivo"]}
        vacio="Sin permisos en el periodo"
        editable={editable}
        onBorrar={(id) => borrar("/escuela/permisos-clase", id, () => { cargar(); onCambio(); })}
        filas={items.map((p) => ({
          id: p.id,
          celdas: [p.fecha, cat.docente(p.empleado_id), `${p.clase_ids.length} clase(s)`, p.con_goce ? "Con goce" : "Sin goce", p.motivo || ""],
        }))}
      />
      <FormDialog open={!!form} titulo="Permiso por clases" onClose={() => setForm(null)} onSubmit={guardar}>
        {form && (
          <>
            <Campo label="Docente" ancho>
              <SelectSimple testid="permiso-docente" value={form.empleado_id} onChange={(v) => setForm({ ...form, empleado_id: v, clase_ids: [] })}
                opciones={cat.docentes.map((d) => [d.id, d.nombre])} />
            </Campo>
            <Campo label="Fecha">
              <Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value, clase_ids: [] })} />
            </Campo>
            <Campo label="Goce de sueldo">
              <SelectSimple value={form.con_goce ? "si" : "no"} onChange={(v) => setForm({ ...form, con_goce: v === "si" })}
                opciones={[["si", "Con goce (se pagan)"], ["no", "Sin goce (no se pagan)"]]} />
            </Campo>
            <Campo label="Clases" ancho>
              {clasesDia.length === 0 ? (
                <div className="text-sm text-slate-500">{form.empleado_id ? "Sin clases ese día" : "Elige docente y fecha"}</div>
              ) : (
                <div className="space-y-1.5">
                  {clasesDia.map((c) => (
                    <label key={c.id} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={form.clase_ids.includes(c.id)}
                        onChange={(e) => setForm({ ...form, clase_ids: e.target.checked ? [...form.clase_ids, c.id] : form.clase_ids.filter((x) => x !== c.id) })}
                        data-testid={`permiso-clase-${c.hora_inicio}`}
                      />
                      <span className="font-mono text-xs">{c.hora_inicio}–{c.hora_fin}</span>
                      {c.tipo === "clase" ? cat.materia(c.materia_id) : c.actividad || "Administrativo"}
                      {c.grupo_id && <span className="text-slate-500">· {cat.grupo(c.grupo_id)}</span>}
                    </label>
                  ))}
                </div>
              )}
            </Campo>
            <Campo label="Motivo" ancho>
              <Input value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} />
            </Campo>
          </>
        )}
      </FormDialog>
    </div>
  );
}

// ---------------- suspensiones ----------------
const ALCANCES = [["escuela", "Toda la escuela"], ["centro", "Un plantel"], ["turno", "Un turno de un plantel"], ["grupo", "Un grupo"]];

export function SuspensionesTab({ desde, hasta, cat, editable, onCambio }) {
  const [items, cargar] = useListado("/escuela/suspensiones", desde, hasta);
  const [form, setForm] = useState(null);

  const guardar = async () => {
    await api.post("/escuela/suspensiones", conEmpresa(form));
    toast.success("Suspensión registrada");
    setForm(null);
    cargar();
    onCambio();
  };

  const alcanceTexto = (s) => ({
    escuela: "Toda la escuela",
    centro: cat.centro(s.centro_id),
    turno: `${cat.centro(s.centro_id)} · ${s.turno}`,
    grupo: `Grupo ${cat.grupo(s.grupo_id)}`,
  }[s.alcance]);

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <p className="text-sm text-slate-600">Clases que la escuela no imparte (consejo técnico, simulacro, contingencia). No cuentan como falta.</p>
        {editable && (
          <Button className="bg-brand hover:bg-brand-hover" onClick={() => setForm({ fecha_inicio: desde, fecha_fin: desde, alcance: "escuela", motivo: "", se_paga: false })} data-testid="suspension-nueva">
            <Plus size={16} className="mr-2" /> Nueva suspensión
          </Button>
        )}
      </div>
      <Tabla
        testid="suspensiones-tabla"
        columnas={["Fechas", "Alcance", "Motivo", "Se paga"]}
        vacio="Sin suspensiones en el periodo"
        editable={editable}
        onBorrar={(id) => borrar("/escuela/suspensiones", id, () => { cargar(); onCambio(); })}
        filas={items.map((s) => ({
          id: s.id,
          celdas: [s.fecha_inicio === s.fecha_fin ? s.fecha_inicio : `${s.fecha_inicio} a ${s.fecha_fin}`, alcanceTexto(s), s.motivo, s.se_paga ? "Sí" : "No"],
        }))}
      />
      <FormDialog open={!!form} titulo="Suspensión de clases" onClose={() => setForm(null)} onSubmit={guardar}>
        {form && (
          <>
            <Campo label="Desde"><Input type="date" value={form.fecha_inicio} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} /></Campo>
            <Campo label="Hasta"><Input type="date" value={form.fecha_fin} onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })} /></Campo>
            <Campo label="Alcance" ancho>
              <SelectSimple value={form.alcance} onChange={(v) => setForm({ ...form, alcance: v, centro_id: null, turno: null, grupo_id: null })} opciones={ALCANCES} />
            </Campo>
            {["centro", "turno"].includes(form.alcance) && (
              <Campo label="Plantel">
                <SelectSimple value={form.centro_id} onChange={(v) => setForm({ ...form, centro_id: v, turno: null })} opciones={cat.centros.map((c) => [c.id, c.nombre])} />
              </Campo>
            )}
            {form.alcance === "turno" && (
              <Campo label="Turno">
                <SelectSimple value={form.turno} onChange={(v) => setForm({ ...form, turno: v })} opciones={cat.turnos(form.centro_id).map((t) => [t, t])} />
              </Campo>
            )}
            {form.alcance === "grupo" && (
              <Campo label="Grupo">
                <SelectSimple value={form.grupo_id} onChange={(v) => setForm({ ...form, grupo_id: v })} opciones={cat.grupos.map((g) => [g.id, g.nombre])} />
              </Campo>
            )}
            <Campo label="Motivo" ancho><Input value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} data-testid="suspension-motivo" /></Campo>
            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <input type="checkbox" checked={form.se_paga} onChange={(e) => setForm({ ...form, se_paga: e.target.checked })} />
              Las clases suspendidas se pagan a los docentes
            </label>
          </>
        )}
      </FormDialog>
    </div>
  );
}

// ---------------- clases extra / reposiciones ----------------
export function ExtrasTab({ desde, hasta, cat, editable, onCambio }) {
  const [items, cargar] = useListado("/escuela/clases-extra", desde, hasta);
  const [form, setForm] = useState(null);

  const guardar = async () => {
    await api.post("/escuela/clases-extra", conEmpresa(form));
    toast.success("Clase registrada");
    setForm(null);
    cargar();
    onCambio();
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <p className="text-sm text-slate-600">Reposiciones y clases extra autorizadas. El docente checa como en cualquier clase y se paga aparte de lo programado.</p>
        {editable && (
          <Button className="bg-brand hover:bg-brand-hover" data-testid="extra-nueva" onClick={() => setForm({
            empleado_id: "", fecha: desde, tipo_extra: "reposicion", centro_id: cat.centros[0]?.id || "", hora_inicio: "", hora_fin: "", materia_id: "", grupo_id: null, motivo: "",
          })}>
            <Plus size={16} className="mr-2" /> Nueva clase
          </Button>
        )}
      </div>
      <Tabla
        testid="extras-tabla"
        columnas={["Fecha", "Horario", "Docente", "Tipo", "Materia", "Motivo"]}
        vacio="Sin clases extra en el periodo"
        editable={editable}
        onBorrar={(id) => borrar("/escuela/clases-extra", id, () => { cargar(); onCambio(); })}
        filas={items.map((x) => ({
          id: x.id,
          celdas: [x.fecha, `${x.hora_inicio}–${x.hora_fin}`, cat.docente(x.empleado_id), x.tipo_extra === "reposicion" ? "Reposición" : "Extra",
            `${cat.materia(x.materia_id)}${x.grupo_id ? ` · ${cat.grupo(x.grupo_id)}` : ""}`, x.motivo || ""],
        }))}
      />
      <FormDialog open={!!form} titulo="Clase extra o reposición" onClose={() => setForm(null)} onSubmit={guardar}>
        {form && (
          <>
            <Campo label="Docente" ancho>
              <SelectSimple value={form.empleado_id} onChange={(v) => setForm({ ...form, empleado_id: v })} opciones={cat.docentes.map((d) => [d.id, d.nombre])} />
            </Campo>
            <Campo label="Tipo">
              <SelectSimple value={form.tipo_extra} onChange={(v) => setForm({ ...form, tipo_extra: v })} opciones={[["reposicion", "Reposición"], ["extra", "Clase extra"]]} />
            </Campo>
            <Campo label="Fecha"><Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} /></Campo>
            <Campo label="Inicio"><Input type="time" value={form.hora_inicio} onChange={(e) => setForm({ ...form, hora_inicio: e.target.value })} /></Campo>
            <Campo label="Fin"><Input type="time" value={form.hora_fin} onChange={(e) => setForm({ ...form, hora_fin: e.target.value })} /></Campo>
            <Campo label="Plantel">
              <SelectSimple value={form.centro_id} onChange={(v) => setForm({ ...form, centro_id: v })} opciones={cat.centros.map((c) => [c.id, c.nombre])} />
            </Campo>
            <Campo label="Materia">
              <SelectSimple value={form.materia_id} onChange={(v) => setForm({ ...form, materia_id: v })} opciones={cat.materias.map((m) => [m.id, m.nombre])} />
            </Campo>
            <Campo label="Grupo">
              <SelectSimple value={form.grupo_id || SIN} onChange={(v) => setForm({ ...form, grupo_id: v === SIN ? null : v })}
                opciones={[[SIN, "Sin grupo"], ...cat.grupos.map((g) => [g.id, g.nombre])]} />
            </Campo>
            <Campo label="Motivo"><Input value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} /></Campo>
          </>
        )}
      </FormDialog>
    </div>
  );
}

// ---------------- suplencias ----------------
const ESTADO_SUPLENCIA = {
  automatica: { label: "Automática", cls: "bg-emerald-100 text-emerald-800" },
  manual: { label: "Asignada por RH", cls: "bg-blue-100 text-blue-800" },
  por_asignar: { label: "Por asignar", cls: "bg-amber-100 text-amber-800" },
  sin_marcas: { label: "Asignada sin marcas", cls: "bg-red-100 text-red-800" },
};

export function SuplenciasTab({ desde, hasta, cat, editable, recargar, onCambio }) {
  const [items, cargar] = useListado("/escuela/suplencias", desde, hasta, recargar);
  const [asignar, setAsignar] = useState(null);

  const guardar = async () => {
    await api.post("/escuela/suplencias", conEmpresa({
      fecha: asignar.fecha, titular_id: asignar.titular_id, clase_id: asignar.clase_id, suplente_id: asignar.suplente_id,
    }));
    toast.success("Suplencia asignada");
    setAsignar(null);
    cargar();
    onCambio();
  };

  const quitar = (id) => borrar("/escuela/suplencias", id, () => { cargar(); onCambio(); });

  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">
        Clases de titulares ausentes que otro docente cubrió checando entrada y salida. Si solo un docente pudo haberla cubierto se acredita sola; si hay varios, elige quién.
      </p>
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="data-table" data-testid="suplencias-tabla">
          <thead><tr><th>Fecha</th><th>Clase</th><th>Titular</th><th>Suplente</th><th>Estado</th>{editable && <th></th>}</tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="text-center text-slate-500 py-8">Sin suplencias en el periodo</td></tr>}
            {items.map((s) => {
              const e = ESTADO_SUPLENCIA[s.estado] || { label: s.estado, cls: "" };
              return (
                <tr key={`${s.fecha}-${s.clase_id}`}>
                  <td className="whitespace-nowrap">{s.fecha}</td>
                  <td>
                    <div className="font-mono text-xs">{s.hora_inicio}–{s.hora_fin}</div>
                    <div className="text-xs text-slate-500">{cat.materia(s.materia_id)}{s.grupo_id ? ` · ${cat.grupo(s.grupo_id)}` : ""}</div>
                  </td>
                  <td>{cat.docente(s.titular_id)}</td>
                  <td>{s.suplente_id ? cat.docente(s.suplente_id) : <span className="text-slate-500 text-xs">Candidatos: {s.candidatos.map(cat.docente).join(", ") || "ninguno"}</span>}</td>
                  <td><span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${e.cls}`}>{e.label}</span></td>
                  {editable && (
                    <td className="whitespace-nowrap">
                      {s.candidatos.length > 0 && (
                        <Button variant="outline" size="sm" onClick={() => setAsignar({ ...s, suplente_id: s.suplente_id || "" })} data-testid={`suplencia-asignar-${s.fecha}-${s.hora_inicio}`}>
                          {s.estado === "por_asignar" ? "Asignar" : "Cambiar"}
                        </Button>
                      )}
                      {s.asignacion_id && <Button variant="ghost" size="sm" onClick={() => quitar(s.asignacion_id)} title="Quitar asignación"><Trash size={16} className="text-red-600" /></Button>}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <FormDialog open={!!asignar} titulo="¿Quién cubrió la clase?" onClose={() => setAsignar(null)} onSubmit={guardar}>
        {asignar && (
          <Campo label={`${asignar.fecha} · ${asignar.hora_inicio}–${asignar.hora_fin} · titular ${cat.docente(asignar.titular_id)}`} ancho>
            <SelectSimple testid="suplencia-suplente" value={asignar.suplente_id} onChange={(v) => setAsignar({ ...asignar, suplente_id: v })}
              opciones={asignar.candidatos.map((id) => [id, cat.docente(id)])} />
          </Campo>
        )}
      </FormDialog>
    </div>
  );
}
